import React from "react";
import { Box, Typography, Divider, Grid } from "@mui/material";
import { formatAbhaId, formatDate } from "../../../../helpers/commonHelper";

const PatientRegistrationDetails = ({ patientData }) => {
  if (!patientData) {
    return <Typography sx={{ p: 2 }}>No patient registration data available.</Typography>;
  }

  const assessment = patientData?.assessment || {};

  // Helper to map 0, 1, 2 or string equivalents to Yes/No/Unknown
  const getLabel = (val) => {
    const stringVal = String(val).toLowerCase();
    if (stringVal === "1" || stringVal === "yes") return "Yes";
    if (stringVal === "0" || stringVal === "no") return "No";
    if (stringVal === "2" || stringVal === "unknown") return "Unknown";
    return val || "N/A";
  };

  const isYes = (val) => {
    const stringVal = String(val).toLowerCase();
    return stringVal === "1" || stringVal === "yes";
  };

  const habitsConfig = [
    { key: "habit_smoking", label: "Smoking" },
    { key: "habit_smokeless", label: "Smokeless" },
    { key: "habit_betelnut_with_tobacco", label: "Betelnut With Tobacco" },
    { key: "habit_alcohol", label: "Alcohol" },
  ];

  const comorbiditiesConfig = [
    { key: "como_diabetes", label: "Diabetes" },
    { key: "como_hypertension", label: "Hypertension" },
    { key: "como_ischemic_heart_disease", label: "Ischemic Heart Disease" },
    { key: "como_asthma_copd", label: "Asthma/COPD" },
    { key: "como_stroke", label: "Stroke" },
    { key: "como_chronic_hepatitisb", label: "Chronic Hepatitis B" },
    { key: "como_msald", label: "MSALD" },
    { key: "como_chronic_kidney_disease", label: "Chronic Kidney Disease" },
    { key: "como_hiv_aids", label: "HIV/AIDS" },
    { key: "como_hypothyroidism", label: "Hypothyroidism" },
    { key: "como_others", label: "Others" },
  ];

  const FieldRow = ({ label, value, duration }) => (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 3 }}>
          <label className="form-label" style={{ width: '250px' }}>{label}</label>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <p className="view-value" style={{ width: '150px' }}>
            {getLabel(value)}
          </p>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <label className="form-label">Duration (Months):</label>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <p className="view-value" style={{ width: '150px' }}>
            {isYes(value) ? (duration || "—") : "—"}
          </p>
        </Grid>
      </Grid>
    </>
  );

  return (
    <Box className="tp-section" sx={{ p: 2 }}>
      {/* ---------- PATIENT DETAILS ---------- */}
      <h5 className="mb-3">Patient Details</h5>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <label className="form-label">Full Name</label>
          <p className="view-value">{patientData?.full_name || "N/A"}</p>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <label className="form-label">Date of Birth</label>
          <p className="view-value">{formatDate(patientData?.dob)}</p>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <label className="form-label">Gender</label>
          <p className="view-value">{patientData?.gender || "N/A"}</p>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <label className="form-label">Mobile Number</label>
          <p className="view-value">{patientData?.mobile || "N/A"}</p>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <label className="form-label">Alternate Mobile</label>
          <p className="view-value">{patientData?.alternate_mobile || "—"}</p>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <label className="form-label">ABHA ID</label>
          <p className="view-value">{formatAbhaId(patientData?.abha)}</p>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <label className="form-label">Address</label>
          <p className="view-value">{patientData?.address || "N/A"}</p>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <label className="form-label">Marital Status</label>
          <p className="view-value">{patientData?.marital_status || "N/A"}</p>
        </Grid>
      </Grid>

      {/* ---------- HABITS ---------- */}
      <h5 className="my-3">Habits</h5>
      <Box sx={{ mb: 4, bgcolor: '#f9f9f98f', p: 2, borderRadius: 1 }}>
        {habitsConfig.map((habit) => (
          <FieldRow
            key={habit.key}
            label={habit.label}
            value={assessment[habit.key]}
            duration={assessment[`${habit.key}_duration`]}
          />
        ))}
      </Box>

      {/* ---------- COMORBIDITIES ---------- */}
      <h5 className="my-3">Comorbidities</h5>
      <Box sx={{ mb: 4, bgcolor: '#f9f9f98f', p: 2, borderRadius: 1 }}>
        {comorbiditiesConfig.map((item) => (
          <FieldRow
            key={item.key}
            label={item.label}
            value={assessment[item.key]}
            duration={assessment[`${item.key}_duration`]}
          />
        ))}
      </Box>

      {/* ---------- FAMILY HISTORY ---------- */}
      <h5 className="my-3">History of Family Cancer</h5>
      <Box sx={{ bgcolor: '#f9f9f98f', p: 3, borderRadius: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: isYes(assessment?.history_family_cancer) ? 2 : 0 }}>
          <label className="form-label">Family History of Cancer</label>
          <p className="view-value ms-5 w-25">{getLabel(assessment?.history_family_cancer)}</p>
        </Box>

        {isYes(assessment?.history_family_cancer) && (
          <Grid container spacing={3} sx={{ mt: 1, pl: 4, borderLeft: '3px solid #1976d2' }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <label className="form-label">Site of Tumor</label>
              <p className="view-value">{assessment?.history_site_of_tumor || "N/A"}</p>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <label className="form-label">Relation</label>
              <p className="view-value">{assessment?.history_relation_of_tumor || "N/A"}</p>
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default PatientRegistrationDetails;
